import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import formatDateWithTimezone from "../TimezoneDateFormatter/TimezoneDateFormatter";
import { formatUSD } from "../../utils/usFormat";

const FILE_TOO_LARGE_MESSAGE = "File size too large";

const generatePDF = async (quotesData, action, timezone = "America/New_York", dateFormat = "MM/DD/YYYY", timeFormat = "12h") => {
  const doc = new jsPDF();
  const greyColor = [82, 86, 89];
  doc.setFontSize(12);
  doc.text("RECIPIENT:", 14, 33);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  const firstName = quotesData?.customer?.FirstName || "";
  const lastName = quotesData?.customer?.LastName || "";
  const customerName = `${firstName} ${lastName}`.trim();
  doc.text(customerName, 14, 39);
  doc.setFont("helvetica", "normal");
  doc.text(`${quotesData?.location?.Address}`, 14, 44);
  doc.text(
    `${
      quotesData?.location?.City +
      " " +
      quotesData?.location?.State +
      " " +
      quotesData?.location?.Country
    }`,
    14,
    49
  );
  doc.text(`${quotesData?.location?.Zip}`, 14, 54);
  doc.setFillColor(...greyColor);
  doc.rect(140, 25, 56, 10, "F");
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(`Quote #${quotesData?.QuoteNumber}`, 141, 31);

  doc.setFillColor(...greyColor);
  doc.rect(140, 45, 56, 10, "F");
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text("Sent on", 141, 41);
  doc.text(`${formatDateWithTimezone(quotesData?.updatedAt, timezone, dateFormat, timeFormat, false)}`, 175, 41);
  doc.setTextColor(255, 255, 255);
  doc.text("Total", 141, 51);
  doc.text(`${quotesData?.Total}`, 180, 51);
  const { products } = quotesData;
  let bodyData = [];
  products.forEach((product) => {
    bodyData.push([
      `${product?.Name || "-"}`,
      `${product?.Description || "-"}`.trim(),
      `${
        product?.Unit
          ? product?.Unit
          : product?.Square
          ? product?.Square
          : product?.Fixed
          ? product?.Fixed
          : product?.Hourly || null
      }`,
      `$${
        product?.CostPerHour
          ? Number(product?.CostPerHour).toFixed(2)
          : product?.CostPerSquare
          ? Number(product?.CostPerSquare).toFixed(2)
          : product?.CostPerFixed
          ? Number(product?.CostPerFixed).toFixed(2)
          : Number(product?.CostPerUnit).toFixed(2) || "-"
      }`,
      `$${product?.Total || "-"}`,
    ]);
  });
  autoTable(doc, {
    startY: 60,
    head: [["Product/Service", "Description", "Qty.", `Unit Price`, "Total"]],
    body: bodyData,
    columnStyles: {
      3: { cellWidth: 30 },
      4: { fontStyle: "bold" },
    },
    headStyles: { fillColor: greyColor },
    margin: { top: 10 },
    createdHeader: function (data) {
      data.table.body[0][3].text = "Unit Price";
    },
  });
  const pageWidth = doc?.internal?.pageSize?.width;
  const pageHeight = doc?.internal?.pageSize?.height;
  const rightMargin = 15;
  const bottomMargin = 30;

  const discountAmount = (quotesData?.Discount / 100) * quotesData?.SubTotal;
  const gstAmount =
    (quotesData?.Tax / 100) * (quotesData?.SubTotal - discountAmount);

  const totalFields = [
    { label: "Subtotal", value: formatUSD(quotesData?.SubTotal), isBold: true },
    {
      label: `Discount(${quotesData?.Discount}%)`,
      value: formatUSD(discountAmount),
      isBold: false,
    },
    {
      label: `Tax(${quotesData?.Tax}%)`,
      value: formatUSD(gstAmount),
      isBold: false,
    },
    { label: "Total", value: formatUSD(quotesData?.Total), isBold: true },
  ];

  const boxHeight = 9;
  const boxWidth = 35;
  const boxX = pageWidth - rightMargin - boxWidth;
  let boxY = pageHeight - bottomMargin - totalFields?.length * boxHeight;

  doc.setDrawColor(0, 0, 0);
  totalFields.forEach((field) => {
    const text = field?.label;
    const amount = field?.value;

    if (field?.isBold) {
      doc.setFont("helvetica", "bold");
    } else {
      doc.setFont("helvetica", "normal");
    }

    doc.rect(boxX, boxY, boxWidth, boxHeight);

    const amountWidth = doc.getTextWidth(amount);
    const amountX = boxX + boxWidth / 2 - amountWidth / 2;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(text, boxX - doc.getTextWidth(text) - 5, boxY + 7);
    doc.text(amount, amountX, boxY + 7);

    boxY += boxHeight;
  });
  const topMargin = 250;
  doc.text(
    "This quote is valid for the next 30 days, after which values may be",
    13,
    topMargin
  );
  doc.text("subject to change.", 13, topMargin + 7);
  if (quotesData?.signature) {
    const signatureY = pageHeight - 40;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    doc.line(13, signatureY - 5, 40, signatureY - 5);
    doc.line(40, signatureY - 5, 110, signatureY - 5);

    doc.text("Date", 19, signatureY);
    doc.text("Client Signature", 55, signatureY);
    const cdnUrl = import.meta.env.VITE_BASE_API;

    const imgData = `${cdnUrl}/v1/cdn/upload/${quotesData?.signature}`;
    doc.addImage(imgData, "JPG", 55, signatureY - 16, 40, 10);

    doc.text(
      `${formatDateWithTimezone(quotesData?.updatedAt, timezone, dateFormat, timeFormat, false)}`,
      14,
      signatureY - 6
    );
  }
  if (action === "download") {
    doc.save("quote.pdf");
  } else if (action === "print") {
    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  }
};
const postFile = async (file) => {
  const cdnUrl = import.meta.env.VITE_BASE_API;
  
  try {
    const fileData = new FormData();

    if (Array.isArray(file)) {
      file.forEach((f) => fileData.append("files", f));
    } else {
      fileData.append("files", file);
    }
    
    const res = await axios.post(`${cdnUrl}/v1/cdn/upload`, fileData);
    
    const strings = Array.isArray(file)
      ? res.data.files.map((file) => file?.filename)
      : res.data.files[0]?.filename;
   
    return strings;
  } catch (error) {
    const isFileTooLarge =
      error.response?.status === 413 ||
      (error.response?.status === 400 && /file size too large/i.test((error.response?.data?.message || "").toString()));
    if (isFileTooLarge) {
      toast.warning(FILE_TOO_LARGE_MESSAGE, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: "file-too-large-toast",
      });
    }
    console.error("Error: ", error.message);
    throw error;
  }
};

const postOneFile = async (file) => {
  const cdnUrl = import.meta.env.VITE_BASE_API;
  let fileNames = [];

  try {
    const fileData = new FormData();

    if (Array.isArray(file)) {
      file.forEach((f) => {
        fileData.append("files", f);
      });
    } else {
      fileData.append("files", file);
    }

    const res = await axios.post(`${cdnUrl}/v1/cdn/upload`, fileData);

    if (res?.data && res?.data?.files && res?.data?.files?.length > 0) {
      fileNames = Array.isArray(file) 
        ? res?.data?.files?.map((file) => file?.filename) 
        : [res?.data?.files[0]?.filename];
    } else {
      throw new Error("No files returned in response");
    }

    return fileNames;
  } catch (error) {
    const isFileTooLarge =
      error.response?.status === 413 ||
      (error.response?.status === 400 && /file size too large/i.test((error.response?.data?.message || "").toString()));
    if (isFileTooLarge) {
      toast.warning(FILE_TOO_LARGE_MESSAGE, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: "file-too-large-toast",
      });
    }
    console.error("Error uploading files:", error.message);
    throw error;
  }
};


const putFile = async (name, file) => {
  const cdnUrl = import.meta.env.VITE_BASE_API;
  try {
    const fileData = new FormData();
    fileData.append("files", file);
    const res = await axios.put(`${cdnUrl}/v1/cdn/upload/${name}`, fileData);
    return res.data;
  } catch (error) {
    const isFileTooLarge =
      error.response?.status === 413 ||
      (error.response?.status === 400 && /file size too large/i.test((error.response?.data?.message || "").toString()));
    if (isFileTooLarge) {
      toast.warning(FILE_TOO_LARGE_MESSAGE, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: "file-too-large-toast",
      });
    }
    console.error("Error: ", error.message);
    throw error;
  }
};

const deleteFile = async (name) => {
  const cdnUrl = import.meta.env.VITE_BASE_API;
  try {
    const res = await axios.delete(`${cdnUrl}/v1/cdn/upload/${name}`);
    return res.data;
  } catch (error) {
    console.error("Error: ", error.message);
  }
};
export { generatePDF, postFile, putFile, deleteFile, postOneFile };
